/**
 * "We don't know" is not "no".
 *
 * The route guards decide what a signed-in user may see from three answers:
 * their roles, their co-producer memberships and their subscription. When the
 * backend is unreachable those answers are unknown — and the app used to read
 * unknown as "no": an outage sent a paying producer to /upgrade, and an empty
 * roles list made them "not a producer" even after the database came back.
 *
 * These helpers keep the distinction explicit so a guard can say "can't tell
 * you yet" instead of guessing.
 */

/** Raised by a query that could not get its answer at all (error or timeout). */
export class BackendUnavailableError extends Error {
  constructor(what: string) {
    super(`Could not load ${what}`);
    this.name = "BackendUnavailableError";
  }
}

export interface QueryAvailability {
  /** The query failed (including our own timeout). */
  isError: boolean;
  /** React Query parked the fetch because the browser is offline. */
  isPaused: boolean;
  /** A value is available — freshly fetched or still in cache. */
  hasData: boolean;
}

/**
 * True when a query has no answer AND can't currently get one.
 *
 * A cached answer always wins: someone who is already working must not be
 * thrown out by one failed refresh. And a paused (offline) query counts as
 * unknown — it resolves to neither data nor error, which is how an offline
 * producer silently ended up on /upgrade.
 */
export function isAnswerUnknown({ isError, isPaused, hasData }: QueryAvailability): boolean {
  return (isError || isPaused) && !hasData;
}

/** True when any of the answers a route guard needs is unknown. */
export function isAuthDataUnknown(queries: QueryAvailability[]): boolean {
  return queries.some(isAnswerUnknown);
}
